import React, { useState } from 'react';
import { useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';
import { object, string } from 'yup';
import { type ApplicationInfo, type ChiefSupervisorFromValues } from './type';
import { PAGE_TEXT } from '../../lang';
import {
  type SupervisorValue,
  useCreateNomination,
  useDeclineNomination,
  useUpdateNomination,
} from '../../../graphql/NominationForm';
import { NominationModules, SupervisorRoleTypes } from '../../../constants';
import {
  Accordion,
  Alert,
  type AlertDetails,
  Button,
  Checkbox,
  Dialog,
  type DialogDetails,
  Select,
  type SelectOption,
  TextField,
} from '../../../../../components';
import { titleTypeOptions } from '../../../../Application/components/ApplicationForm/AcademicReferee/options';
import { APPLICATION_STATUS_TYPE } from '../../../../../utils/statusConstants/statusConstants';

const CHIEF_SUPERVISOR_TEXT = PAGE_TEXT.CHIEF_SUPERVISOR.CHIEF_SUPERVISOR;

type ChiefSupervisorBoxProps = {
  supervisorData: ChiefSupervisorFromValues;
  applicationInfo: ApplicationInfo;
  isViewMode: boolean;
  departmentOptions: SelectOption[];
  coSupervisors: SupervisorValue[];
  refetchNominationInformation: () => void;
  onMoveNext: (activatedId: NominationModules) => void;
};

export const ChiefSupervisorBox: React.FunctionComponent<
  ChiefSupervisorBoxProps
> = props => {
  const { nominationId = '' } = useParams();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDetails, setDialogDetails] = useState<DialogDetails>({
    title: '',
    content: '',
  });

  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });
  const {
    isViewMode,
    supervisorData,
    applicationInfo,
    departmentOptions,
    coSupervisors,
    onMoveNext,
    refetchNominationInformation,
  } = props;

  const { applicantId, status, applicationId } = applicationInfo;

  const hasAccepted = Boolean(nominationId);
  const hasDeclined = status === APPLICATION_STATUS_TYPE.IN_NOMINATION_POOL;
  const updateNomination = useUpdateNomination(refetchNominationInformation);
  const handleUpdateSupervisors = async (
    value: ChiefSupervisorFromValues,
  ): Promise<void> => {
    try {
      const chiefSup: SupervisorValue = {
        type: SupervisorRoleTypes.CHIEF_SUPERVISOR,
        ...value,
      };
      await updateNomination({
        variables: {
          id: nominationId,
          input: {
            // to keep co supervisor data (if any)
            supervisors: [chiefSup, ...coSupervisors],
          },
        },
      });
      setAlertDetails({
        alertContent: 'Updated Supervisor Information Successfully.',
        alertType: 'success',
      });
      refetchNominationInformation();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setAlertDetails({
        alertContent:
          'Updated Supervisor Information Failed.' + (e as Error).message,
        alertType: 'error',
      });
    }
    setOpenAlert(true);
  };

  const createNomination = useCreateNomination();
  const declineNomination = useDeclineNomination();
  const handleAcceptApplication = async (): Promise<void> => {
    try {
      // create a nomination
      const nominationRes = await createNomination({
        variables: {
          input: {
            application: applicationId,
            applicant: applicantId,
            supervisors: [
              {
                type: SupervisorRoleTypes.CHIEF_SUPERVISOR,
                ...formik.values,
              },
            ],
          },
        },
      });
      const nominationId = nominationRes.data?.createNomination._id;
      if (nominationId !== undefined) {
        window.location.href = `/internal/nomination/${nominationId}`;
      }
    } catch (e) {
      setAlertDetails({
        alertContent: `Error Occurs.` + (e as Error)?.message,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  };

  const handleDeclineApplication = (): void => {
    setDialogOpen(false);

    // put this application into Pool
    declineNomination({
      variables: {
        applicationId,
      },
    })
      .then(() => {
        setDialogDetails(dialogMappings[PAGE_TEXT.CLOSE]);
        setDialogOpen(true);
        refetchNominationInformation();
      })
      .catch(e => {
        setAlertDetails({
          alertContent: (e as Error)?.message,
          alertType: 'error',
        });
        setOpenAlert(true);
      });
  };
  const handleBackTolist = (): void => {
    window.location.href = `/internal/nominations/application-list`;
  };

  const formik = useFormik<ChiefSupervisorFromValues>({
    initialValues: supervisorData,
    validationSchema: object({
      title: string().required('Required'),
      jobTitle: string().required('Required'),
      surname: string().required('Required'),
      givenName: string().required('Required'),
      department: string().required('Required'),
      email: string().email('Invalid email format').required('Required'),
    }),
    onSubmit: async value => {
      if (hasAccepted) {
        await handleUpdateSupervisors(value);
      } else {
        setDialogDetails(dialogMappings[PAGE_TEXT.ACCEPT]);
        setDialogOpen(true);
      }
    },
  });

  const dialogMappings: Record<string, DialogDetails> = {
    [PAGE_TEXT.ACCEPT]: {
      title: PAGE_TEXT.ACCEPT_CONFIRM.TITLE,
      content: PAGE_TEXT.ACCEPT_CONFIRM.CONTENT,
      confirmAction: PAGE_TEXT.SUBMIT,
      cancelAction: PAGE_TEXT.CANCEL,
      handleDialogConfirmAction: handleAcceptApplication,
    },
    [PAGE_TEXT.DECLINE]: {
      title: PAGE_TEXT.DECLINE_CONFIRM.TITLE,
      content: PAGE_TEXT.DECLINE_CONFIRM.CONTENT,
      confirmAction: PAGE_TEXT.SUBMIT,
      cancelAction: PAGE_TEXT.CANCEL,
      handleDialogConfirmAction: handleDeclineApplication,
    },
    [PAGE_TEXT.CLOSE]: {
      title: '',
      content: PAGE_TEXT.DECLINE_SUCCESS,
      confirmAction: PAGE_TEXT.BACK,
      handleDialogConfirmAction: handleBackTolist,
    },
  };

  const handleMoveNext = (): void => {
    onMoveNext(NominationModules.SUPERVISOR_TEAM);
  };

  const handleOpenDeclineDialog = (): void => {
    setDialogDetails(dialogMappings[PAGE_TEXT.DECLINE]);
    setDialogOpen(true);
  };
  const handleCloseDialog = (): void => {
    setDialogOpen(false);
  };

  const handleClosePage = (): void => {
    window.close();
  };

  // keep the order align with the supervisor team page
  // netID can never be edited
  // other field can be edited, except in view mode or has declined
  const initialForm: Array<{
    label: string;
    disabled: boolean;
    fieldtype: 'text' | 'select';
    options?: SelectOption[];
    fieldName: keyof ChiefSupervisorFromValues;
  }> = [
    {
      label: CHIEF_SUPERVISOR_TEXT.NET_ID,
      disabled: true,
      fieldtype: 'text',
      fieldName: 'netId',
    },
    {
      label: CHIEF_SUPERVISOR_TEXT.DEPARTMENT,
      disabled: isViewMode || hasDeclined,
      options: departmentOptions,
      fieldtype: 'select',
      fieldName: 'department',
    },
    {
      label: CHIEF_SUPERVISOR_TEXT.GIVEN_NAME,
      disabled: isViewMode || hasDeclined,
      fieldtype: 'text',
      fieldName: 'givenName',
    },
    {
      label: CHIEF_SUPERVISOR_TEXT.SURNAME,
      disabled: isViewMode || hasDeclined,
      fieldtype: 'text',
      fieldName: 'surname',
    },
    {
      label: CHIEF_SUPERVISOR_TEXT.SUPERVISOR_TITLE,
      disabled: isViewMode || hasDeclined,
      options: titleTypeOptions,
      fieldtype: 'select',
      fieldName: 'title',
    },
    {
      label: CHIEF_SUPERVISOR_TEXT.JOB_TITLE,
      disabled: isViewMode || hasDeclined,
      fieldtype: 'text',
      fieldName: 'jobTitle',
    },
    {
      label: CHIEF_SUPERVISOR_TEXT.EMAIL,
      disabled: isViewMode || hasDeclined,
      fieldtype: 'text',
      fieldName: 'email',
    },
  ];

  const checkboxContent: Array<{
    label: string;
    disabled: boolean;
    value: boolean;
    fieldName: keyof ChiefSupervisorFromValues;
  }> = [
    {
      label: CHIEF_SUPERVISOR_TEXT.PREVIOUS_SUCCESSFUL_SUPERVISION,
      disabled: isViewMode || hasDeclined,
      fieldName: 'previousSuccessfulSupervision',
      value: formik.values.previousSuccessfulSupervision,
    },
    {
      label: CHIEF_SUPERVISOR_TEXT.DSRC_CHAIR,
      disabled: isViewMode || hasDeclined,
      fieldName: 'isDrcChair',
      value: formik.values.isDrcChair,
    },
    {
      label: CHIEF_SUPERVISOR_TEXT.DORPGS,
      disabled: isViewMode || hasDeclined,
      fieldName: 'isDoRPgS',
      value: formik.values.isDoRPgS,
    },
  ];

  return (
    <Accordion
      style={{
        width: '100%',
      }}
      disableExpand
      title={CHIEF_SUPERVISOR_TEXT.TITLE}
      defaultExpanded
      details={
        <div>
          <Dialog
            openDialog={dialogOpen}
            width={'700px'}
            dialogTitle={dialogDetails.title}
            dialogAction={dialogDetails.confirmAction}
            cancelAction={dialogDetails.cancelAction ?? undefined}
            onDialogAction={dialogDetails.handleDialogConfirmAction}
            onDialogClose={handleCloseDialog}
          >
            <Typography margin={'0rem 1.5rem 2rem'}>
              {dialogDetails.content}
            </Typography>
          </Dialog>
          <Alert
            openAlert={openAlert}
            setOpenAlert={setOpenAlert}
            alertDetails={alertDetails}
          />
          <form onSubmit={formik.handleSubmit} noValidate>
            <Box padding={2} marginTop={1}>
              <Grid container spacing={2}>
                {initialForm.map((item, index) => {
                  return (
                    <Grid key={index} size={6}>
                      {item.fieldtype === 'select' ? (
                        <Select
                          fullWidth
                          options={item.options ?? []}
                          label={item.label}
                          name={item.fieldName}
                          required
                          value={formik.values[item.fieldName] ?? ''}
                          InputLabelProps={{ shrink: true }}
                          disabled={item.disabled}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched[item.fieldName] &&
                            Boolean(formik.errors[item.fieldName])
                          }
                          helperText={
                            formik.touched[item.fieldName] &&
                            formik.errors[item.fieldName]
                          }
                        />
                      ) : (
                        <TextField
                          fullWidth
                          type="text"
                          required
                          label={item.label}
                          name={item.fieldName}
                          value={formik.values[item.fieldName]}
                          InputLabelProps={{ shrink: true }}
                          disabled={item.disabled}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched[item.fieldName] &&
                            Boolean(formik.errors[item.fieldName])
                          }
                          helperText={
                            formik.touched[item.fieldName] &&
                            formik.errors[item.fieldName]
                          }
                        />
                      )}
                    </Grid>
                  );
                })}

                <Grid size={12}>
                  {checkboxContent.map((item, index) => (
                    <Checkbox
                      key={index}
                      label={item.label}
                      name={item.fieldName}
                      value={item.value}
                      disabled={item.disabled}
                      onChange={formik.handleChange}
                    />
                  ))}
                </Grid>
              </Grid>
              {!isViewMode && (
                <Box
                  display={'flex'}
                  justifyContent={'center'}
                  marginY={5}
                  gap={4}
                >
                  {!hasAccepted ? (
                    <>
                      <Button
                        style={{ marginRight: '15px' }}
                        variant="contained"
                        type="submit"
                      >
                        {PAGE_TEXT.ACCEPT}
                      </Button>
                      <Button
                        variant="outlined"
                        type="button"
                        onClick={handleOpenDeclineDialog}
                      >
                        {PAGE_TEXT.DECLINE}
                      </Button>
                    </>
                  ) : (
                    <>
                      {/* enable to update info after acceptance */}
                      <Button
                        className={hasAccepted ? '' : 'hidden'}
                        variant="contained"
                        type="submit"
                      >
                        {PAGE_TEXT.UPDATE}
                      </Button>
                      <Button
                        className={hasAccepted ? '' : 'hidden'}
                        variant="outlined"
                        onClick={hasDeclined ? handleClosePage : handleMoveNext}
                        type="button"
                      >
                        {hasDeclined ? PAGE_TEXT.CLOSE : PAGE_TEXT.NEXT}
                      </Button>
                    </>
                  )}
                </Box>
              )}
            </Box>
          </form>
        </div>
      }
    />
  );
};
